import { makeObservable, observable, action, runInAction, toJS } from "mobx";
import { SearchRequest } from "../api/Types";
import {
  PerPageSettings,
  WithBooleanFlag,
  Sorter,
  Searcher,
  Filter,
  ExpandableKeys,
  StatusInfo,
} from "./core";
import loggingApi from "../api/Logging";
import dictionaryStore from "./dictionary";
import { SortingDirection } from "../constants/Enums";
import Constants from "../constants/Common";

class LoggingPermissionStore {
  data = [];

  isLoading = new WithBooleanFlag();
  paginator = new PerPageSettings();
  sorter = new Sorter();
  filter: Filter = new Filter();
  selectedRows = new ExpandableKeys();

  search = new Searcher();

  logsStatus = new StatusInfo();

  abortController = null;

  constructor() {
    makeObservable(
      this,
      {
        data: observable,
        getPermissionLogs: action,
      },
      {
        deep: true,
      },
    );
    this.sorter.setSortData("created", SortingDirection.Desc);
  }

  getPermissionLogs = async () => {
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
    this.logsStatus.resetStatus();
    try {
      this.isLoading.setTrue();
      const request: SearchRequest = {
        page: this.paginator.page,
        count: this.paginator.perPage,
        sortItems: this.sorter.sortData,
        filterItems: this.filter.filterItems,
        search: this.search.getSearch(),
      };

      const res = await loggingApi.getPermissionLogs(request, {
        signal: this.abortController.signal,
      });
      if (!this.abortController.signal.aborted) {
        runInAction(() => {
          this.data = res.data.items.map((item, index) => {
            return {
              ...item,
              appModule: dictionaryStore.data["AppModule"].find(
                (dictItem) => dictItem.id === item.module,
              ),
            };
          });
          this.paginator.updateMeta(res.data);
        });
      this.isLoading.setFalse();
    }
    } catch (e) {
      if(e.message !== 'canceled') {
        this.isLoading.setFalse();
        this.logsStatus.setDetails(e?.response?.data?.errors);
        this.logsStatus.setStatus(Constants.requestStatuses.error);
      }
    }
    return toJS(this.data);
  };

  deletePermissionLogs = async () => {
    this.isLoading.setTrue();
    this.logsStatus.resetStatus();
    try {
      await loggingApi.deletePermissionLogsByIds(this.selectedRows.keys);
      this.logsStatus.setStatus(Constants.requestStatuses.deleted);
      await this.getPermissionLogs();
    } catch (e) {
      this.logsStatus.setDetails(e?.response.data?.errors);
      this.logsStatus.setStatus(Constants.requestStatuses.error);
    } finally {
      this.isLoading.setFalse();
    }
  };
}

const loggingPermissionStore = new LoggingPermissionStore();
export default loggingPermissionStore;
